import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { rebuildPermissionsForUsers } from '@chancenwerk/server/rebuildPermissionsForUsers.js';
import { DateTime } from 'luxon';
import type { Filter } from 'mongodb';
import { ChancenRole } from './ChancenRole.js';

/**
 * @entity SemanticRelationship
 * This class is an abstract definition of an semantic relationship
 */
@Entity()
export class SemanticRelationship extends MongoEntity {
  public static override _class = 'SemanticRelationship';
  public static override _collectionName = 'semantic-relationships';

  @Field(type => DateTime, {
    default: v => v ?? DateTime.now(),
  })
  public createdAt: Maybe<DateTime>;

  @Field(type => ID)
  public predicateID: Maybe<string>;

  @DataLoadedField(type => ChancenRole, ({ predicateID }) => predicateID)
  public predicate: Maybe<ChancenRole>;

  @Field(type => ID)
  public subjectID: Maybe<string>;

  @Field(type => MongoEntity, {
    resolve: function (this: SemanticRelationship) {
      return async () => {
        throw new Error(
          'Used the resolver on the Base SemanticRelationship Class! You need to override them for the specific relationship you are using.',
        );
      };
    },
  })
  public subject: Maybe<MongoEntity>;

  @Field(type => ID)
  public objectID: Maybe<string>;

  @Field(type => MongoEntity, {
    resolve: function (this: SemanticRelationship) {
      return async () => {
        throw new Error(
          'Used the resolver on the Base SemanticRelationship Class! You need to override them for the specific relationship you are using.',
        );
      };
    },
  })
  public object: Maybe<MongoEntity>;

  @Dependencies<typeof SemanticRelationship>({
    classes: async () => [SemanticRelationship],
    ids: async function (this: PartialEntityData<SemanticRelationship>) {
      if (!this._id) return [];
      return [this._id];
    },
  })
  @Field(type => Boolean, {
    resolve: function (this: SemanticRelationship) {
      return async () => {
        const con = constructValue(this) as SemanticRelationship;
        return con.isActive();
      };
    },
  })
  public active: Maybe<boolean>;

  /**
   * @description It saves the entity with all business logic, but **doesn't** update the roles on the user
   */
  override save(): Promise<number> {
    return super.save();
  }

  static override async beforeCreate(entity: SemanticRelationship): Promise<void> {
    if (!entity.isValid()) {
      throw new Error('Diese Beziehung ist nicht korrekt!');
    }
    if (await entity.isDuplicate(false)) {
      throw new Error('Diese Beziehung gibt es bereits!');
    }
    return super.beforeCreate(entity);
  }

  @Query(type => [SemanticRelationship])
  public static getRelationshipsByPredicates<T extends SemanticRelationship>(
    @Arg('predicateID', type => String) predicateID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<T>) => {
      const col = await getRawCollection<EntityData<TestStart>>(SemanticRelationship._collectionName);
      const relationships = await col.find({ predicateID }).toArray();
      return relationships.map(r => constructValue(r) as T);
    };
  }

  @Query(type => [SemanticRelationship])
  public static getRelationshipsBySubject<T extends SemanticRelationship>(
    @Arg('subjectID', type => String) subjectID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<T>) => {
      const col = await getRawCollection<EntityData<T>>(SemanticRelationship._collectionName);
      const relationships = await col.find({ subjectID }).toArray();
      return relationships.map(r => constructValue(r) as T);
    };
  }

  @Query(type => [SemanticRelationship])
  public static getRelationshipsByObject<T extends SemanticRelationship>(
    @Arg('objectID', type => String) objectID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<T>) => {
      const col = await getRawCollection<EntityData<T>>(SemanticRelationship._collectionName);
      const relationships = await col.find({ objectID }).toArray();
      return relationships.map(r => constructValue(r) as T);
    };
  }

  /**
   *
   * @param taetigkeiten The Relationships to be saved. Can have different subjects
   * @returns
   */
  @Mutation(type => Int)
  public static saveSemanticRelationshipsAndUpdateRoles(
    @Arg('relationships', type => [SemanticRelationship]) relationships: SemanticRelationship[],
  ) {
    return async (gqlFields: void) => {
      const usersToUpdateRoles = new Set<string>();

      for (const relationship of relationships) {
        // Save taetigkeit
        if (!relationship._id) {
          await relationship.create();
        } else {
          await relationship.save();
        }

        // Add the subject to the user to update roles for
        if (!relationship.subjectID)
          throw new Error('Taetigkeit got saved. But the subjectID is not set! ' + relationship._id);

        usersToUpdateRoles.add(relationship.subjectID);
      }

      await rebuildPermissionsForUsers(Array.from(usersToUpdateRoles.values()));
    };
  }

  @Mutation(type => Boolean)
  public static updatePermissionsForUser(@Arg('userID', type => String) userID: string) {
    return async () => {
      // clean up permissions. Remove duplicates because the merging put some in.
      const coll = await getRawCollection<EntityData<SemanticRelationship>>(SemanticRelationship._collectionName);
      const all = await coll.find({ subjectID: userID }).toArray();

      const seen: Array<EntityData<SemanticRelationship>> = [];

      for (const rel of all) {
        const duplicate = seen.find(
          r => r.subjectID === rel.subjectID && r.objectID === rel.objectID && r.predicateID === rel.predicateID,
        );
        if (duplicate) {
          await coll.deleteOne({ _id: rel._id });
        } else {
          seen.push(rel);
        }
      }

      await rebuildPermissionsForUsers([userID]);
    };
  }

  /**
   * Checks if the relationship is valid. (If all connections are filled)
   * Checks whether the resulting update is a duplicate against all other relationships exluding itself.
   * @param oldEntity
   * @param update
   * @returns
   */
  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: SemanticRelationship,
  ): Promise<void> {
    if (!update.isValid()) {
      throw new Error('Diese Beziehung ist nicht korrekt!');
    }
    if (await update.isDuplicate(false)) {
      throw new Error('Diese Beziehung gibt es bereits!');
    }

    return super.beforeUpdate(oldEntity, update);
  }

  public async isDuplicate(checkSelf: boolean = true) {
    const relationshipCollection = await getRawCollection<SemanticRelationship>(SemanticRelationship._collectionName);
    const duplicateRelationship = await relationshipCollection.findOne({
      predicateID: this.predicateID,
      subjectID: this.subjectID,
      objectID: this.objectID,
    });

    if (!checkSelf && duplicateRelationship?._id === this._id) return false;
    if (duplicateRelationship) return true;
    return false;
  }

  public getContext(): string {
    throw new Error(
      'Tried to get Context from the Base SemanticRelationship Class! You need to override them for the specific relationship you are using.',
    );
  }

  public isActive() {
    return true;
  }

  public static isActiveMongoSelector(time: DateTime = DateTime.now()): Filter<EntityData<SemanticRelationship>> {
    return {};
  }

  /**
   * Check if the relationship is valid
   * @returns whether the relationship is valid. In the base case it is valid if the subject, predicate and the object are all filled.
   */
  public isValid(): boolean {
    return !!this.predicateID && !!this.subjectID && !!this.objectID;
  }

  static override async afterRemove(id: string, entityData: SemanticRelationship): Promise<void> {
    await rebuildPermissionsForUsers([entityData.subjectID ?? '']);
    return super.afterRemove(id, entityData);
  }
}

if (A.isServer) {
  void (async () => {
    const coll = await getRawCollection<SemanticRelationship>(SemanticRelationship._collectionName);
    await coll.createIndex({ objectID: 1 });
    await coll.createIndex({ subjectID: 1 });
    await coll.createIndex({ predicateID: 1 });
  })();
}
